// COLORES QUE NO SE VEN EN UN CORREO — se quitan antes de guardar.
//
// Johanna Arosemena (Johanna & Otto Arosemena), 2026-10-30: *"al enviar por
// campaña, veo que sale el texto inicial de color casi transparente"*.
//
// El editor de correos (EmailRichEditor) escribe sobre el fondo oscuro del CRM,
// con la letra casi blanca (#eef6ff). Al aplicar una fuente o un tamaño, el
// navegador copia ese color dentro del texto (`<span style="color: rgb(238,
// 246, 255)">`), y en el buzón del cliente —fondo blanco— la primera línea sale
// casi invisible. El editor no tiene botón de color: ningún color claro lo
// eligió la persona.
//
// La MISMA regla está en el servidor (deskte-api/src/utils/coloresDelCorreo.ts),
// que la aplica al vestir cada correo; aquí se aplica al guardar, para que lo
// guardado ya salga limpio. `coloresDelCorreo.test.ts` vigila que digan lo mismo.

const NOMBRES_CLAROS = new Set(['white', 'snow', 'ghostwhite', 'whitesmoke', 'ivory', 'azure', 'aliceblue', 'mintcream', 'honeydew', 'transparent']);

/** El color de un valor CSS como [r, g, b, a], o null si no se sabe leer. */
function leerColor(valor) {
  let v = String(valor || '').trim().toLowerCase().replace(/\s*!important\s*$/, '');
  // var(--c-white, #eef6ff): en el correo no hay variables; si acaso, vale el respaldo.
  const conVar = v.match(/^var\(\s*--[\w-]+\s*(?:,\s*(.+))?\)$/);
  if (conVar) v = (conVar[1] || '').trim();
  if (!v) return null;
  if (NOMBRES_CLAROS.has(v)) return v === 'transparent' ? [0, 0, 0, 0] : [255, 255, 255, 1];
  let m = v.match(/^#([0-9a-f]{3,8})$/);
  if (m) {
    let h = m[1];
    if (h.length === 3 || h.length === 4) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6 && h.length !== 8) return null;
    const n = (i) => parseInt(h.slice(i, i + 2), 16);
    return [n(0), n(2), n(4), h.length === 8 ? n(6) / 255 : 1];
  }
  m = v.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+%?))?\s*\)$/);
  if (m) {
    const a = m[4] === undefined ? 1 : m[4].endsWith('%') ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
    return [Number(m[1]), Number(m[2]), Number(m[3]), a];
  }
  return null;
}

/** ¿Este color de LETRA se ve sobre un correo blanco? */
export function colorDeLetraInvisible(valor) {
  const crudo = String(valor || '').trim().toLowerCase();
  if (/^var\(/.test(crudo) && !leerColor(crudo)) return true; // variable sin respaldo: no existe fuera del CRM
  const c = leerColor(crudo);
  if (!c) return false;
  const [r, g, b, a] = c;
  if (a < 0.5) return true;
  const lin = (x) => { const s = x / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
  const luz = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  // Contraste con el blanco por debajo de ~1.6: no se lee.
  return (1.05) / (luz + 0.05) < 1.6;
}

/** ¿Este fondo viene del tema oscuro del CRM? (una variable del tema) */
function fondoDelTema(valor) {
  return /^var\(/.test(String(valor || '').trim().toLowerCase());
}

/**
 * Quita de los `style="…"` del correo los colores de letra que no se verían
 * sobre blanco y los fondos que son variables del tema del CRM. Todo lo demás
 * (negritas, fuente, tamaño, alineación, colores oscuros) se queda igual.
 */
export function quitarColoresInvisibles(html) {
  const s = String(html ?? '');
  if (!/style\s*=/i.test(s)) return s;
  return s.replace(/\sstyle\s*=\s*(["'])([\s\S]*?)\1/gi, (todo, q, css) => {
    const quedan = String(css).split(';').filter((decl) => {
      const i = decl.indexOf(':');
      if (i < 0) return decl.trim() !== '';
      const prop = decl.slice(0, i).trim().toLowerCase();
      const val = decl.slice(i + 1);
      if (prop === 'color') return !colorDeLetraInvisible(val);
      if (prop === 'background-color' || prop === 'background') return !fondoDelTema(val);
      return true;
    }).map((d) => d.trim()).filter(Boolean);
    return quedan.length ? ` style=${q}${quedan.join('; ')}${q}` : '';
  });
}
